import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search, LogOut, RefreshCw, Trash2, Mail, Phone, MapPin, Calendar,
  Filter, User, Package, Inbox, CheckCircle2, XCircle, Clock, FileText, BookOpen, Settings, Briefcase,
  ChevronDown, Images, LayoutGrid, MessageSquare, Megaphone, ReceiptIndianRupee, FileSpreadsheet, Users, BarChart3, ScrollText,
} from 'lucide-react';
import { API } from '../api';
import BlogManager from '../components/BlogManager';
import PackageManager from '../components/PackageManager';
import SiteContentManager from '../components/SiteContentManager';
import PagesManager from '../components/PagesManager';
import ChatFAQManager from '../components/ChatFAQManager';
import GalleryManager from '../components/GalleryManager';
import TaxonomyManager from '../components/TaxonomyManager';
import BannerManager from '../components/BannerManager';
import InvoicesManager from '../components/InvoicesManager';
import InvoiceSettingsManager from '../components/InvoiceSettingsManager';
import ItineraryBuilder from '../components/ItineraryBuilder';
import GroupEnquiriesManager from '../components/GroupEnquiriesManager';
import LandingPagesManager from '../components/LandingPagesManager';
import CampaignAnalytics from '../components/CampaignAnalytics';
import VoucherRegister from '../components/VoucherRegister';
import SEOSettingsManager from '../components/SEOSettingsManager';

const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'converted', label: 'Converted' },
  { key: 'closed', label: 'Closed' },
];

const STATUS_STYLES = {
  new:       { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-500',   icon: Inbox },
  contacted: { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500',  icon: Clock },
  converted: { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500',  icon: CheckCircle2 },
  closed:    { bg: 'bg-gray-100',  text: 'text-gray-600',   dot: 'bg-gray-400',   icon: XCircle },
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [err, setErr] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('travelsera_admin_token') : null;

  useEffect(() => {
    if (!token) {
      nav('/admin');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/enquiries`, {
        headers: { 'X-Admin-Token': token },
      });
      setEnquiries(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('travelsera_admin_token');
        nav('/admin');
        return;
      }
      setErr('Failed to load enquiries.');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const prev = enquiries;
    setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, {
        headers: { 'X-Admin-Token': token },
      });
    } catch (e) {
      setEnquiries(prev);
    }
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry? This cannot be undone.')) return;
    const prev = enquiries;
    setEnquiries((list) => list.filter((e) => e.id !== id));
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, {
        headers: { 'X-Admin-Token': token },
      });
    } catch (e) {
      setEnquiries(prev);
    }
  };

  const logout = () => {
    localStorage.removeItem('travelsera_admin_token');
    nav('/admin');
  };

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      if (filter !== 'all' && (e.status || 'new') !== filter) return false;
      if (sourceFilter !== 'all' && (e.source || 'unknown') !== sourceFilter) return false;
      if (!query) return true;
      const hay = [e.name, e.phone, e.email, e.message, e.package_title]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(query.toLowerCase());
    });
  }, [enquiries, filter, sourceFilter, query]);

  const counts = useMemo(() => {
    const c = { all: enquiries.length, new: 0, contacted: 0, converted: 0, closed: 0 };
    enquiries.forEach((e) => { c[e.status || 'new'] = (c[e.status || 'new'] || 0) + 1; });
    return c;
  }, [enquiries]);

  const sourceCounts = useMemo(() => {
    const m = new Map();
    enquiries.forEach((e) => {
      const s = e.source || 'unknown';
      m.set(s, (m.get(s) || 0) + 1);
    });
    // Sort by count desc so the busiest campaign appears first.
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [enquiries]);

  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem('admin_active_tab') || 'enquiries';
    } catch (e) {
      return 'enquiries';
    }
  });
  const [openMenu, setOpenMenu] = useState(null); // 'content' | 'settings' | null
  const menuRef = useRef(null);

  // Persist the active tab across browser refreshes.
  useEffect(() => {
    try { localStorage.setItem('admin_active_tab', tab); } catch (e) {}
  }, [tab]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Grouped nav definition — each top-level tab is either single or a dropdown group
  const NAV = [
    { kind: 'single', key: 'enquiries', label: 'Enquiries', icon: Inbox },
    { kind: 'single', key: 'group-enquiries', label: 'Group Enquiries', icon: Users },
    { kind: 'group', key: 'packages', label: 'Packages', icon: Briefcase, items: [
      { key: 'packages', label: 'Packages', icon: Briefcase, hint: 'Add or edit holiday packages' },
      { key: 'itineraries', label: 'Itinerary Builder', icon: FileSpreadsheet, hint: 'Day-wise trip documents with flights, hotels & payment' },
      { key: 'voucher-register', label: 'Voucher Register', icon: ScrollText, hint: 'Every issued trip voucher — search & reissue' },
    ]},
    { kind: 'group', key: 'billing', label: 'Billing', icon: ReceiptIndianRupee, items: [
      { key: 'invoices', label: 'Invoices', icon: FileSpreadsheet, hint: 'Generate India-format invoices, download & share' },
      { key: 'invoice-settings', label: 'Invoice Settings', icon: Settings, hint: 'Company GSTIN, bank, terms & numbering' },
    ]},
    { kind: 'group', key: 'content', label: 'Content', icon: LayoutGrid, items: [
      { key: 'blogs', label: 'Blog Posts', icon: BookOpen, hint: 'Write travel guides & articles' },
      { key: 'pages', label: 'Static Pages', icon: FileText, hint: 'About, Terms, Privacy, etc.' },
      { key: 'landing-pages', label: 'Landing Pages', icon: Megaphone, hint: 'Marketing campaign pages at /p/{slug}' },
      { key: 'campaign-analytics', label: 'Campaign Analytics', icon: BarChart3, hint: 'Rank landing pages by conversion' },
      { key: 'gallery', label: 'Media Gallery', icon: Images, hint: 'All uploaded images' },
    ]},
    { kind: 'group', key: 'settings', label: 'Settings', icon: Settings, items: [
      { key: 'site', label: 'Site Content', icon: LayoutGrid, hint: 'Hero, benefits, partners, testimonials, branding' },
      { key: 'banners', label: 'Offer Banners', icon: Megaphone, hint: 'Sliding banners on the home page' },
      { key: 'taxonomies', label: 'Destinations & Themes', icon: MapPin, hint: 'Add or edit destinations and travel themes' },
      { key: 'chatbot', label: 'Chat FAQs', icon: MessageSquare, hint: 'Rules for the Sera chat widget' },
      { key: 'seo-settings', label: 'SEO & Sharing', icon: Search, hint: 'Global page title, meta description & OG image' },
    ]},
  ];

  const activeGroupKey = useMemo(() => {
    for (const item of NAV) {
      if (item.kind === 'group' && item.items.some((s) => s.key === tab)) return item.key;
    }
    return null;
    // NAV is defined inline in render — safe to omit; only `tab` changes matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 select-none" data-testid="admin-logo">
              <div className="text-[20px] font-extrabold leading-none tracking-tight text-[#0d64a8]">
                travel<span className="text-[#ffcc29]">sera</span>
              </div>
              <span className="text-xs text-gray-400 border-l border-gray-200 pl-3 hidden sm:inline">Admin Panel</span>
            </div>
            <nav ref={menuRef} className="flex items-center gap-1 ml-4" data-testid="admin-top-nav">
              {NAV.map((it) => {
                const Icon = it.icon;
                if (it.kind === 'single') {
                  const active = tab === it.key;
                  return (
                    <button
                      key={it.key}
                      onClick={() => { setTab(it.key); setOpenMenu(null); }}
                      data-testid={`tab-${it.key}`}
                      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md ${active ? 'bg-[#e8f2fb] text-[#0d64a8] font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <Icon size={14} /> {it.label}
                    </button>
                  );
                }
                const active = activeGroupKey === it.key;
                const open = openMenu === it.key;
                return (
                  <div key={it.key} className="relative">
                    <button
                      onClick={() => setOpenMenu(open ? null : it.key)}
                      data-testid={`tab-group-${it.key}`}
                      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md ${active || open ? 'bg-[#e8f2fb] text-[#0d64a8] font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                      aria-expanded={open}
                      aria-haspopup="true"
                    >
                      <Icon size={14} /> {it.label}
                      <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div
                        className="absolute left-0 top-full mt-1 min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg py-1.5 z-30"
                        data-testid={`menu-${it.key}`}
                        role="menu"
                      >
                        {it.items.map((sub) => {
                          const SubIcon = sub.icon;
                          const isActive = tab === sub.key;
                          return (
                            <button
                              key={sub.key}
                              onClick={() => { setTab(sub.key); setOpenMenu(null); }}
                              data-testid={`tab-${sub.key}`}
                              role="menuitem"
                              className={`w-full flex items-start gap-2.5 text-left px-3 py-2 text-sm ${isActive ? 'bg-[#e8f2fb] text-[#0d64a8]' : 'text-gray-700 hover:bg-gray-50'}`}
                            >
                              <SubIcon size={14} className="mt-0.5 shrink-0" />
                              <span className="min-w-0">
                                <span className="block font-medium">{sub.label}</span>
                                {sub.hint && <span className="block text-[11px] text-gray-400 mt-0.5">{sub.hint}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {tab === 'enquiries' && (
              <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0d64a8] px-3 py-1.5 rounded-md hover:bg-gray-50">
                <RefreshCw size={14} /> Refresh
              </button>
            )}
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-6">
        {tab === 'blogs' && <BlogManager />}
        {tab === 'packages' && <PackageManager />}
        {tab === 'site' && <SiteContentManager />}
        {tab === 'pages' && <PagesManager />}
        {tab === 'landing-pages' && <LandingPagesManager />}
        {tab === 'campaign-analytics' && <CampaignAnalytics />}
        {tab === 'chatbot' && <ChatFAQManager />}
        {tab === 'gallery' && <GalleryManager />}
        {tab === 'taxonomies' && <TaxonomyManager />}
        {tab === 'banners' && <BannerManager />}
        {tab === 'invoices' && <InvoicesManager />}
        {tab === 'invoice-settings' && <InvoiceSettingsManager />}
        {tab === 'itineraries' && <ItineraryBuilder />}
        {tab === 'voucher-register' && <VoucherRegister />}
        {tab === 'seo-settings' && <SEOSettingsManager />}
        {tab === 'group-enquiries' && <GroupEnquiriesManager />}
        {tab === 'enquiries' && (
        <div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enquiries</h1>
            <p className="text-sm text-gray-500">Follow up with travellers and update their status.</p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, email, package..."
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm outline-none focus:border-[#0d64a8] w-[300px]"
            />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {STATUSES.map((s) => {
            const active = filter === s.key;
            const style = s.key !== 'all' ? STATUS_STYLES[s.key] : null;
            const Icon = style?.icon || Filter;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`text-left border rounded-lg px-4 py-3 transition ${
                  active
                    ? 'border-[#0d64a8] bg-[#e8f2fb] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-[#0d64a8]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-gray-500">{s.label}</span>
                  <Icon size={14} className={active ? 'text-[#0d64a8]' : 'text-gray-400'} />
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{counts[s.key] ?? 0}</div>
              </button>
            );
          })}
        </div>

        {/* Source filter chips — one per campaign/entry point */}
        {sourceCounts.length > 0 && (
          <div className="mb-5" data-testid="enquiry-source-filter">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone size={13} className="text-gray-400" />
              <span className="text-[11px] uppercase tracking-widest text-gray-500 font-medium">Filter by campaign / source</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setSourceFilter('all')}
                data-testid="source-chip-all"
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  sourceFilter === 'all'
                    ? 'bg-[#0d64a8] text-white border-[#0d64a8]'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-[#0d64a8]/50'
                }`}
              >
                All sources <span className="opacity-70">· {enquiries.length}</span>
              </button>
              {sourceCounts.map(([src, cnt]) => {
                const active = sourceFilter === src;
                const isCampaign = !['floating', 'detail_page', 'footer', 'unknown', 'qa_admin_nav'].includes(src);
                return (
                  <button
                    key={src}
                    onClick={() => setSourceFilter(src)}
                    data-testid={`source-chip-${src}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      active
                        ? 'bg-[#0d64a8] text-white border-[#0d64a8]'
                        : isCampaign
                          ? 'bg-[#fff8e1] text-[#a06400] border-[#ffcc29]/60 hover:border-[#ffcc29]'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-[#0d64a8]/50'
                    }`}
                  >
                    {isCampaign && <span className="mr-1">📣</span>}
                    {src.replace(/_/g, ' ')}
                    <span className="opacity-70"> · {cnt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {err && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2 rounded-md mb-4">{err}</div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-gray-500 text-sm">Loading enquiries...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-200 rounded-lg">
            <Inbox className="mx-auto text-gray-300 mb-2" size={42} />
            <p className="text-gray-600 font-medium">No enquiries match your filter</p>
            <p className="text-sm text-gray-400 mt-1">Try clearing the search or switching status.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((e) => {
              const st = STATUS_STYLES[e.status || 'new'];
              const SIcon = st.icon;
              return (
                <div key={e.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-[280px]">
                      <div className="w-10 h-10 rounded-full bg-[#0d64a8]/10 text-[#0d64a8] flex items-center justify-center font-semibold shrink-0">
                        {(e.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{e.name}</h3>
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>
                            <SIcon size={11} /> {(e.status || 'new').toUpperCase()}
                          </span>
                          {e.source && (
                            <span className="text-[10px] uppercase tracking-widest text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">
                              {e.source.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mt-1">
                          <span className="flex items-center gap-1"><Phone size={11}/> {e.phone}</span>
                          {e.email && <span className="flex items-center gap-1"><Mail size={11}/> {e.email}</span>}
                          <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(e.created_at)}</span>
                        </div>
                        {e.package_title && (
                          <div className="flex items-center gap-1 text-xs text-[#0d64a8] mt-1.5">
                            <Package size={12} /> {e.package_title}
                            {e.package_id && <span className="text-gray-400">#{e.package_id}</span>}
                          </div>
                        )}
                        {e.message && (
                          <p className="text-sm text-gray-700 mt-2 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 leading-relaxed">
                            {e.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <select
                        value={e.status || 'new'}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        className="text-xs border border-gray-200 rounded-md px-2 py-1.5 outline-none focus:border-[#0d64a8] bg-white"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                      <a href={`tel:${e.phone}`} title="Call" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]">
                        <Phone size={13} />
                      </a>
                      {e.email && (
                        <a href={`mailto:${e.email}`} title="Email" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0d64a8] hover:text-[#0d64a8]">
                          <Mail size={13} />
                        </a>
                      )}
                      <a href={`https://wa.me/${e.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" title="WhatsApp" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#25D366] hover:text-[#25D366]">
                        <User size={13} />
                      </a>
                      <button onClick={() => removeEnquiry(e.id)} title="Delete" className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-500">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
